import React, { useState, useEffect } from 'react';
import { useProcurement } from '@/context/ProcurementContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  CheckCircle, 
  DollarSign,
  Calendar,
  Building2,
  FileText,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

interface VendorInvoiceListProps {
  onViewInvoice?: (invoice: any) => void;
}

export const VendorInvoiceList: React.FC<VendorInvoiceListProps> = ({ onViewInvoice }) => {
  const { state, actions } = useProcurement();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (state.vendorInvoices.length === 0) {
      actions.fetchVendorInvoices();
    }
  }, []);

  const filteredInvoices = state.vendorInvoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (state.vendors.find(v => v.id === invoice.vendorId)?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.status.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'All' || invoice.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'verified':
        return 'default';
      case 'paid':
        return 'default';
      case 'overdue':
        return 'destructive';
      case 'cancelled':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
      case 'paid':
        return <CheckCircle className="h-4 w-4" />;
      case 'overdue':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const handleVerifyInvoice = async (invoiceId: number) => {
    setLoading(true);
    try {
      const success = await actions.verifyVendorInvoice(invoiceId);
      if (success) {
        toast.success('Invoice verified successfully');
      } else {
        toast.error('Failed to verify invoice');
      }
    } catch (error) {
      toast.error('Failed to verify invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId: number) => {
    setLoading(true);
    try {
      const success = await actions.payVendorInvoice(invoiceId);
      if (success) {
        toast.success('Invoice marked as paid');
      } else {
        toast.error('Failed to mark invoice as paid');
      }
    } catch (error) {
      toast.error('Failed to mark invoice as paid');
    } finally {
      setLoading(false);
    }
  };

  const getVendorName = (vendorId: number) => {
    const vendor = state.vendors.find(v => v.id === vendorId);
    return vendor?.name || `Vendor ${vendorId}`;
  };

  const getPONumber = (poId: number) => {
    const po = state.purchaseOrders.find(p => p.id === poId);
    return po?.poNumber || `PO-${poId}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Vendor Invoices</h2>
          <p className="text-muted-foreground">Manage vendor invoices and payments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => actions.fetchVendorInvoices()}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search & Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by invoice number, vendor, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="All">All Status</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice List */}
      <div className="space-y-4">
        {filteredInvoices.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No invoices found</h3>
              <p className="text-muted-foreground text-center">
                {searchTerm || statusFilter !== 'All' 
                  ? 'Try adjusting your search criteria'
                  : 'No vendor invoices have been created yet'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{invoice.invoiceNumber}</h3>
                      <Badge variant={getStatusVariant(invoice.status)} className="flex items-center gap-1">
                        {getStatusIcon(invoice.status)}
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Vendor</p>
                          <p className="font-medium">{getVendorName(invoice.vendorId)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">PO Number</p>
                          <p className="font-medium">{getPONumber(invoice.poId)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Amount</p>
                          <p className="font-medium">{formatCurrency(invoice.totalAmount)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Due Date</p>
                          <p className="font-medium">
                            {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {invoice.notes && (
                      <div className="mt-2">
                        <p className="text-sm text-muted-foreground">Notes:</p>
                        <p className="text-sm">{invoice.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewInvoice?.(invoice)}
                      className="flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      View Details
                    </Button>
                    
                    {invoice.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVerifyInvoice(invoice.id)}
                        disabled={loading}
                        className="flex items-center gap-2 text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Verify
                      </Button>
                    )}
                    
                    {invoice.status === 'verified' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePayInvoice(invoice.id)}
                        disabled={loading}
                        className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <DollarSign className="h-4 w-4" />
                        Mark as Paid
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};